<template>
  <div class="c-home-map-container">
    <div
      v-show="!isMapsUnavailable"
      id="map"
      ref="mapEl"
      class="c-home-map"
      style="width: 100%; height: 100%"
    ></div>

    <div
      v-if="!isLocationGettingError"
      class="c-home-map-container__current-location-button"
    >
      <CurrentLocationButton @click="handleCurrentLocationButtonClick" />
    </div>

    <div
      v-if="!mapInitialized && !isMapsUnavailable"
      class="c-home-map__status"
    >
      <CPreloader size="small" />
    </div>

    <div
      v-if="isMapsUnavailable"
      class="c-home-map__status"
    >
      <IconMapUnavailable class="c-home-map__status__icon" />

      <p>Карта недоступна</p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';

  import type { YMapLocationRequest, YMapMarker, YMapLayer, LngLat } from '@yandex/ymaps3-types';
  import type { YMapDefaultMarker } from '@yandex/ymaps3-default-ui-theme';

  import { useLocation } from '@/composables/useLocation';
  import { useVendingMachinesList } from '@/composables/useVendingMachinesList';
  import { useYMaps } from '@/composables/useYMaps';

  import IconMapUnavailable from '@/components/icons/IconMapUnavailable.vue';
  import { useGeneralMap } from '@/composables/useGeneralMap';

  import { VendingMachine } from '@/api/requests/VendingMachines';

  import CPreloader from '../CPreloader/CPreloader.vue';
  import CurrentLocationButton from './inner/CurrentLocationButton.vue';

  type ActiveMarkerInner = {
    mapMarker: YMapMarker | YMapDefaultMarker;
    circleMarker: YMapMarker;
    contentEl: HTMLElement;
    markerEl: HTMLElement;
    machine: VendingMachine;
  };

  type CircleMarker = {
    marker: YMapMarker;
    content: HTMLElement;
    machine: VendingMachine;
    circle: HTMLElement;
  };

  const CIRCLES_LAYER_Z_INDEX = 1;
  const MARKERS_INFOS_LAYER_Z_INDEX = 2;
  const DEFAULT_MARKER_Z_INDEX = 1;
  const HOVERED_MARKER_Z_INDEX = 1000;
  const HIGHLIGHTED_MARKER_DEFAULT_Z_INDEX = 1001;

  const props = defineProps<{
    highlightedVendingMachineId?: number | null;
    location?: YMapLocationRequest;
  }>();

  const { allVendingMachines, waitForVendingMachines } = useVendingMachinesList();
  const { ymaps3Lib } = useYMaps();
  const { isLocationGettingError, getLocation } = useLocation();
  const { map, initMap } = useGeneralMap({
    location: props.location
  });

  const mapEl = ref<HTMLElement | null>(null);

  const mapInitialized = ref(false);
  const isMapsUnavailable = ref(false);

  async function createMachinesMarkersOnMap() {
    await Promise.all([waitForVendingMachines()]);

    if (!ymaps3Lib.value) {
      return;
    }

    // оставляю чтобы не потерять как это делается
    // const { YMapDefaultMarker } = await import('@yandex/ymaps3-default-ui-theme');

    try {
      const { YMapMarker, YMapLayer } = ymaps3Lib.value;

      const circleMarkers: CircleMarker[] = [];

      const circlesLayer = new YMapLayer({
        type: 'default',
        zIndex: CIRCLES_LAYER_Z_INDEX
      });

      const markersInfosLayer = new YMapLayer({
        type: 'default',
        zIndex: MARKERS_INFOS_LAYER_Z_INDEX
      });

      map.value?.addChild(circlesLayer);
      map.value?.addChild(markersInfosLayer);

      allVendingMachines.value
        ?.filter(machine => machine.attributes.Coordinates)
        .forEach(machine => {
          const content = document.createElement('section');
          content.classList.add('marker-content-wrapper');

          const marker = new YMapMarker(
            {
              coordinates: [
                machine.attributes.Coordinates.coordinates.lng,
                machine.attributes.Coordinates.coordinates.lat
              ],
              draggable: false,
              zIndex: DEFAULT_MARKER_Z_INDEX
            },
            content
          );

          circlesLayer.root?.addChild(marker);

          let markerCircle: HTMLAnchorElement | HTMLDivElement;
          if (props.highlightedVendingMachineId !== machine.id) {
            markerCircle = document.createElement('a');
            markerCircle.href = `/machine/${machine.id}`;
            markerCircle.target = '_blank';

            if (props.highlightedVendingMachineId) {
              markerCircle.classList.add('marker-circle_inactive');
            }
          } else {
            if (machine.attributes.Coordinates) {
              markerCircle = document.createElement('a');
              getLocation().then(location => {
                (markerCircle as HTMLAnchorElement).href = makeLinkForYandexMapsRoute(
                  machine,
                  location
                );
              });
              markerCircle.target = '_blank';
            } else {
              markerCircle = document.createElement('div');
            }

            markerCircle.classList.add('marker-circle_highlighted');

            marker.update({
              zIndex: HIGHLIGHTED_MARKER_DEFAULT_Z_INDEX
            });
          }

          markerCircle.classList.add('marker-circle');

          content.appendChild(markerCircle);

          const circleMarker: CircleMarker = {
            marker,
            content,
            machine,
            circle: markerCircle
          };

          circleMarkers.push(circleMarker);
        });

      let highlightedMarkerControls: {
        showIcon: () => void;
        hideIcon: () => void;
        circleMarkerInfo: CircleMarker;
      } | null = null;

      if (props.highlightedVendingMachineId) {
        const highlightedCircleMarker = circleMarkers.find(
          marker => marker.machine.id === props.highlightedVendingMachineId
        );

        if (highlightedCircleMarker) {
          highlightedMarkerControls = getHighlightCircleMarkerControls(highlightedCircleMarker);

          highlightedMarkerControls.showIcon();
        }
      }

      circleMarkers.forEach(addedMarker => {
        let isMarkerHovered = false;
        let isMarkerInnerHovered = false;

        addedMarker.circle.addEventListener('mouseover', () => {
          const activeMarker = createActiveMarkerInfo(addedMarker.machine, addedMarker.marker);

          activeMarker.contentEl.addEventListener('mouseover', () => {
            isMarkerInnerHovered = true;
          });

          activeMarker.contentEl.addEventListener('mouseout', () => {
            isMarkerInnerHovered = false;

            if (!isMarkerHovered) {
              removeActiveMarkerInner();
            }
          });

          addedMarker.circle.addEventListener('mouseout', mouseOutHandler);

          function removeActiveMarkerInner() {
            removeMarkerInnerFromMap(activeMarker, markersInfosLayer);

            highlightedMarkerControls?.showIcon();
          }

          function mouseOutHandler() {
            if (!isMarkerInnerHovered) {
              removeActiveMarkerInner();
            }

            isMarkerHovered = false;

            addedMarker.circle.removeEventListener('mouseout', mouseOutHandler);
          }

          addedMarker.marker.update({
            zIndex: HOVERED_MARKER_Z_INDEX
          });

          showMarker(activeMarker, markersInfosLayer);

          if (props.highlightedVendingMachineId !== addedMarker.machine.id) {
            highlightedMarkerControls?.hideIcon();
          }
        });
      });
    } catch (error) {
      isMapsUnavailable.value = true;
      // eslint-disable-next-line no-console
      console.error('Failed to initialize map:', error);
    }
  }

  function makeLinkForYandexMapsRoute(machine: VendingMachine, currentLocation: LngLat | null) {
    if (!machine.attributes.Coordinates) {
      return '';
    }

    let from;
    if (currentLocation) {
      from = `${currentLocation[1]},${currentLocation[0]}`;
    } else {
      from = '';
    }

    const to = `${machine.attributes.Coordinates.coordinates.lat},${machine.attributes.Coordinates.coordinates.lng}`;

    const rtext = `${from}~${to}`;

    return `https://yandex.ru/maps/?rtext=${rtext}`;
  }

  function getHighlightCircleMarkerControls(circleMarkerInfo: CircleMarker) {
    let content: HTMLElement | null = null;

    let isIconShown = false;

    return {
      showIcon: () => {
        if (isIconShown) {
          return;
        }

        isIconShown = true;

        circleMarkerInfo.marker.update({
          zIndex: 1001
        });

        content = document.createElement('div');
        content.classList.add('marker-circle-highlighted-pin');

        const imageElement = document.createElement('div');
        imageElement.classList.add('marker-circle-highlighted-pin-image');

        content.appendChild(imageElement);

        circleMarkerInfo.circle.appendChild(content);
      },
      hideIcon: () => {
        if (content) {
          circleMarkerInfo.circle.removeChild(content);
        }

        isIconShown = false;
      },
      circleMarkerInfo
    };
  }

  function showMarker(marker: ActiveMarkerInner, markersInfosLayer: YMapLayer) {
    markersInfosLayer.root?.addChild(marker.mapMarker);

    if (marker.machine.attributes.address) {
      setTimeout(() => {
        marker.markerEl.classList.add('marker-active-inner_visible');
      }, 0);
    }

    marker.circleMarker.update({
      zIndex: 1000
    });
  }

  function removeMarkerInnerFromMap(markerInner: ActiveMarkerInner, markersInfosLayer: YMapLayer) {
    markersInfosLayer.root?.removeChild(markerInner.mapMarker);

    markerInner.markerEl.classList.remove('marker-active-inner_visible');

    markerInner.circleMarker.update({
      zIndex: 1
    });
  }

  function createActiveMarkerInfo(
    machine: VendingMachine,
    circleMarker: YMapMarker
  ): ActiveMarkerInner {
    if (!ymaps3Lib.value) {
      throw new Error('ymaps3Lib is not initialized');
    }

    const { YMapMarker } = ymaps3Lib.value;

    const content = document.createElement('section');

    const mapMarker = new YMapMarker(
      {
        coordinates: [
          machine.attributes.Coordinates.coordinates.lng,
          machine.attributes.Coordinates.coordinates.lat
        ],
        draggable: false,
        zIndex: 800
      },
      content
    );

    const activeMarkerInnerEl = document.createElement('div');
    activeMarkerInnerEl.classList.add('marker-active-inner');

    const markerAddress = document.createElement('div');
    markerAddress.classList.add('marker-active-inner__address');
    markerAddress.textContent = `${machine.attributes.address} ${machine.attributes.address}`;
    activeMarkerInnerEl.appendChild(markerAddress);

    content.appendChild(activeMarkerInnerEl);

    return {
      mapMarker,
      contentEl: content,
      markerEl: activeMarkerInnerEl,
      machine,
      circleMarker
    };
  }

  async function handleCurrentLocationButtonClick() {
    const location = await getLocation();

    if (!location) {
      return;
    }

    map.value?.update({
      location: {
        center: location,
        zoom: 15
      }
    });
  }

  onMounted(async () => {
    await initMap(mapEl.value);

    mapInitialized.value = true;

    createMachinesMarkersOnMap();
  });
</script>

<style>
  .c-home-map-container {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .c-home-map {
    overflow: hidden;
  }

  .marker-circle-highlighted-pin {
    width: 98px;
    height: 98px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ff7b00;
    border-radius: 27.54px;
    position: relative;
    left: -44px;
    bottom: 112px;
    cursor: pointer;
    border: 2px solid #ffffff;
  }

  .marker-circle-highlighted-pin-image {
    background-image: url('/images/map-vending-machine.svg');
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    width: 46px;
    height: 64px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }

  .marker-content-wrapper {
    width: 26px;
    height: 26px;
    /* transform: translate(-50%, -50%); */
  }

  .marker-circle {
    width: 20px;
    height: 20px;
    background-color: #1a6dff;
    border-radius: 50%;
    border: 3px solid #fff;
    transition: all 0.3s ease;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    display: block;
    z-index: 3000;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background-image: url('/images/WT.svg');
      background-size: 12px;
      background-repeat: no-repeat;
      background-position: center;
      z-index: 3000;
    }

    &_active {
      border: 3px solid #fff;
      z-index: 100000;

      &:after {
        display: none;
      }
    }

    &_clickable {
      cursor: pointer;
    }

    &_inactive {
    }

    &_highlighted {
      background-color: #ff7b00;

      &:after {
        background-image: none;
      }
    }
  }

  .marker-active-inner {
    position: absolute;
    left: 10px;
    margin: auto auto;
    display: flex;
    bottom: auto;
    top: auto;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    transition: all 0.3s ease;
    z-index: 2;
    background-color: #ffffffd9;
    border-radius: 10px;
    padding: 4px 8px 4px 14px;
    color: #000;
    transform: translateY(calc(-50% + 16px));
    opacity: 0;
    transition: all 0.3s ease;
    max-width: 240px;
    overflow: hidden;

    &_visible {
      opacity: 1;
    }

    &__address {
      font-size: 12px;
      text-align: center;
      border-radius: 27px;
      padding: 4px 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 600;
      max-width: 100%;
    }
  }

  .c-home-map__status {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    transform: translateY(-50%);

    &__icon {
      width: 100px;
      height: 100px;
    }

    p {
      font-size: 16px;
      margin: 0;
      font-weight: 600;
    }
  }

  .c-home-map-container__current-location-button {
    position: absolute;
    bottom: 44px;
    right: 32px;
  }

  .marker-circle_inactive {
    background-color: #3f84fb;
  }
</style>
